import { useState } from 'react';
import StyledSnackbar from 'src/components/StyledComponents/StyledSnackBar';
import { BasicVendor, Vendor } from 'src/models/Management.interface';
import { apiUrls } from 'src/utils/properties.utils';
import { axiosTokenInstance } from 'src/utils/axiosInstance.utils';
import { getApiErrorMessage } from 'src/utils/errorHandler';

interface BasicVendorResponse {
  id: string;
  organizationName: string;
}

interface PaginatedVendorResponse {
  data: Vendor[];
  totalItems: number;
  currentPage: number;
  totalPages: number;
}

export const useGetVendor = () => {
  const [vendors, setVendors] = useState<Vendor[]>([]);
  const [basicVendors, setBasicVendors] = useState<BasicVendor[]>([]);
  const [loading, setLoading] = useState(true);
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const { showSnackbar } = StyledSnackbar();

  const fetchVendors = async (
    page = 1,
    limit = 100,
    search = '',
    sortBy = 'organizationName',
    order = 'ASC'
  ) => {
    setLoading(true);
    try {
      const response = await axiosTokenInstance.get<PaginatedVendorResponse>(
        apiUrls.VENDOR_MANAGEMENT,
        {
          params: {
            offset: page,
            limit: limit,
            search: search,
            sortBy: sortBy,
            order: order,
          },
          withCredentials: true,
        }
      );

      setVendors(response.data.data || []);
      setTotalItems(response.data.totalItems || 0);
      setTotalPages(response.data.totalPages || 0);
      setCurrentPage(response.data.currentPage || 1);
    } catch (error) {
      const errorMessage = await getApiErrorMessage(error);
      showSnackbar(errorMessage, 'error');
      setVendors([]);
    } finally {
      setTimeout(() => {
        setLoading(false);
      }, 800);
    }
  };

  const deleteVendor = async (vendorId: string) => {
    try {
      const response = await axiosTokenInstance.delete(
        `${apiUrls.DELETE_VENDOR}/${vendorId}`
      );

      setVendors((prevVendors) =>
        prevVendors.filter((vendor) => vendor.id !== vendorId)
      );

      showSnackbar(response.data.message, 'success');

      if (vendors.length === 1) {
        setVendors([]);
      }
    } catch (error) {
      const errorMessage = await getApiErrorMessage(error);
      showSnackbar(errorMessage, 'error');
      fetchVendors();
    }
  };

  const fetchBasicVendors = async () => {
    setLoading(true);
    try {
      const response = await axiosTokenInstance.get<PaginatedVendorResponse>(
        apiUrls.VENDOR_LIST_BASIC
      );

      setBasicVendors(response.data.data || []);
      return response.data.data || [];
    } catch (error) {
      const errorMessage = await getApiErrorMessage(error);
      showSnackbar(errorMessage, 'error');
      setBasicVendors([]);
    } finally {
      setTimeout(() => {
        setLoading(false);
      }, 800);
    }
  };

  return {
    loading,
    vendors,
    totalItems,
    totalPages,
    currentPage,
    basicVendors,
    fetchBasicVendors,
    fetchVendors,
    deleteVendor,
    setVendors,
  };
};

export const getBasicVendors = async (): Promise<BasicVendorResponse[]> => {
  const response = await axiosTokenInstance.get<{
    data: BasicVendorResponse[];
  }>(apiUrls.BASIC_VENDORS);
  return response.data.data || [];
};
